import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

/**
 * Vim-style two-key navigation. Press `g` then a target letter to jump:
 *   g d → /         (Dashboard)
 *   g s → /standings
 *   g c → /calendar
 *   g a → /analysis
 *   g h → /sessions  (history)
 *   g r → /drivers   (racers)
 *   g t → /stats
 *
 * Also handles:
 *   ? → opens a small help popup describing the shortcuts.
 *   Esc → dismisses the help popup.
 *
 * Skips when the user is typing in an input, textarea, contenteditable, or has a modifier held.
 */
const NAV_MAP: Record<string, string> = {
  d: "/",
  s: "/standings",
  c: "/calendar",
  a: "/analysis",
  h: "/sessions",
  r: "/drivers",
  t: "/stats",
};

const HELP_LINES: { keys: string; label: string }[] = [
  { keys: "g d", label: "Dashboard" },
  { keys: "g s", label: "Standings" },
  { keys: "g c", label: "Calendar" },
  { keys: "g a", label: "Analysis" },
  { keys: "g h", label: "Session history" },
  { keys: "g r", label: "Drivers" },
  { keys: "g t", label: "Stats" },
  { keys: "Space", label: "Play / pause loaded replay" },
  { keys: "? / ?", label: "Skip replay backward / forward 10s" },
  { keys: "?", label: "This help" },
  { keys: "Esc", label: "Dismiss" },
];

const HELP_DIALOG_ID = "global-shortcuts-help";

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

function showHelp() {
  if (document.getElementById(HELP_DIALOG_ID)) return;
  const overlay = document.createElement("div");
  overlay.id = HELP_DIALOG_ID;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Keyboard shortcuts");
  // Tailwind utilities instead of inline cssText so the dialog inherits
  // theme tokens (dark/light) and is consistent with the rest of the UI.
  overlay.className =
    "fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) hideHelp();
  });

  const card = document.createElement("div");
  card.className =
    "w-full max-w-sm rounded-xl border border-border bg-card text-card-foreground p-5 shadow-2xl font-sans";
  card.innerHTML = `
    <div class="flex items-center justify-between mb-3">
      <div class="font-semibold text-sm">Keyboard shortcuts</div>
      <button type="button" aria-label="Close"
        class="bg-transparent text-current border-0 cursor-pointer text-lg leading-none">×</button>
    </div>
    <table class="w-full text-[13px] border-collapse">
      ${HELP_LINES.map(
        ({ keys, label }) => `
        <tr>
          <td class="py-1 text-muted-foreground font-mono">${keys}</td>
          <td class="py-1 text-right">${label}</td>
        </tr>
      `,
      ).join("")}
    </table>
  `;
  card.querySelector("button")?.addEventListener("click", hideHelp);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

function hideHelp() {
  document.getElementById(HELP_DIALOG_ID)?.remove();
}

export const useGlobalShortcuts = () => {
  const [, setLocation] = useLocation();
  const prefixActiveRef = useRef(false);
  const prefixTimerRef = useRef<number | null>(null);

  useEffect(() => {
    function clearPrefix() {
      prefixActiveRef.current = false;
      if (prefixTimerRef.current !== null) {
        window.clearTimeout(prefixTimerRef.current);
        prefixTimerRef.current = null;
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      if (e.key === "Escape") {
        if (document.getElementById(HELP_DIALOG_ID)) {
          hideHelp();
          e.preventDefault();
        }
        clearPrefix();
        return;
      }

      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        showHelp();
        e.preventDefault();
        return;
      }

      if (prefixActiveRef.current) {
        const target = NAV_MAP[e.key.toLowerCase()];
        clearPrefix();
        if (target) {
          setLocation(target);
          e.preventDefault();
        }
        return;
      }

      if (e.key === "g") {
        prefixActiveRef.current = true;
        prefixTimerRef.current = window.setTimeout(clearPrefix, 1500);
        e.preventDefault();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearPrefix();
    };
  }, [setLocation]);
};
